import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  DistributeDocumentsUploadProcessDocument,
  DistributeDocumentsUploadProcessMutation,
  DocumentsUploadProcessItemFragment,
  UploadProcessFileUrlsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import NotFound from 'modules/core/components/NotFound'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FileViewer from 'modules/documents/components/FileViewer'
import { ControllerType } from 'modules/documents/components/PdfViewer/types'
import { FileAssignment } from 'modules/documents/lib/FileAssignment'

import AssignerFooter from './AssignerFooter'
import AssignerHeader from './AssignerHeader'
import FilesAssigner from './FilesAssigner'
import HintModal from './HintModal'
import SummaryModal from './SummaryModal'
import { ManualDocumentAssignerContext } from './context'

interface Props {
  uploadProcess: DocumentsUploadProcessItemFragment
}

type FilesUrl = { fileId: number; url: string }
type FileData = NonNullable<DocumentsUploadProcessItemFragment['filesData']>[number]

const ManualDocumentAssigner = ({ uploadProcess }: Props) => {
  const [fileAssignments, setFileAssignments] = useState<FileAssignment[]>([])
  const [filesData, setFilesData] = useState<FileData[]>([])
  const { data: uploadProcessFileUrls } = useQuery(UploadProcessFileUrlsDocument, {
    variables: {
      uploadProcessId: uploadProcess.id,
    },
    pollInterval: 5 * 60 * 1000,
  })
  const [selectedFile, setSelectedFile] = useState<number | undefined>()
  const [selectedPage, setSelectedPage] = useState<number | undefined>()
  const [pagesToDisplay, setPagesToDisplay] = useState<number[] | undefined>(undefined)
  const [loading, setLoading] = useState<boolean | undefined>(false)
  const [payslipsToDistribute, setPayslipsToDistribute] = useState<number | undefined>()
  const [payslipsToDiscard, setPayslipsToDiscard] = useState<number | undefined>()
  const [payslipsNotAssigned, setPayslipsNotAssigned] = useState<number | undefined>()
  const [payslipsToMerge, setPayslipsToMerge] = useState<number | undefined>()
  const [showResumeModal, setShowResumeModal] = useState<boolean>(false)
  const [file, setFile] = useState<FileAssignment | undefined>()
  const [controllers, setControllers] = useState<ControllerType[]>([])
  const [distributionMutation] = useMutation(DistributeDocumentsUploadProcessDocument, {
    extractorFn: (data: DistributeDocumentsUploadProcessMutation) =>
      data.documentsMutations.distributeDocumentsUploadProcess,
  })

  const generateFileAssigment = (fileUrl: FilesUrl, fileData: FileData): FileAssignment => {
    return {
      fileId: fileUrl.fileId,
      filename: fileData.filename,
      pages: fileData.pages,
      contentType: fileData.contentType,
      url: fileUrl.url,
    }
  }

  useUnsafeEffect(() => {
    const assignments = uploadProcessFileUrls?.documents.uploadProcessFileUrlsConnection.nodes
      ?.map((fileUrl) => {
        const fileData = uploadProcess.filesData?.find((f) => f.fileId === fileUrl.fileId)

        if (!fileData) {
          return
        }

        return generateFileAssigment(fileUrl, fileData)
      })
      .filter(Boolean)
    setFileAssignments(assignments || [])
    setFilesData(uploadProcess.filesData || [])
    if (assignments && assignments.length > 0 && !selectedFile) {
      setSelectedFile(assignments[0]?.fileId)
      setSelectedPage(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadProcessFileUrls, uploadProcess])

  useUnsafeEffect(() => {
    setFile(fileAssignments.find((f) => f.fileId === selectedFile))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile])

  const context = useMemo(
    () => ({
      loading: loading,
      setLoading: setLoading,
      selectedFileId: selectedFile,
      selectedPage: selectedPage,
      payslipsToDistribute: payslipsToDistribute,
      setPayslipsToDistribute: setPayslipsToDistribute,
      payslipsToDiscard: payslipsToDiscard,
      setPayslipsToDiscard: setPayslipsToDiscard,
      payslipsNotAssigned: payslipsNotAssigned,
      setPayslipsNotAssigned: setPayslipsNotAssigned,
      payslipsToMerge: payslipsToMerge,
      setPayslipsToMerge: setPayslipsToMerge,
    }),
    [
      payslipsNotAssigned,
      payslipsToDiscard,
      payslipsToDistribute,
      payslipsToMerge,
      selectedFile,
      selectedPage,
      loading,
    ]
  )

  const onClickPageFile = useUnsafeCallback(
    (fileId: number, page: number) => {
      setSelectedFile(fileId)
      setSelectedPage(page)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadProcess]
  )

  const distribute = async () => {
    const response = await distributionMutation({
      variables: {
        id: uploadProcess.id,
      },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('split_documents.workflow_payslips.manual_document_assigner.on_distribute'),
      })
    }
  }

  useEffect(() => {
    if (uploadProcess.uploadType === 'manual_per_page') {
      setPagesToDisplay(selectedPage !== undefined ? [selectedPage] : [])
      setControllers(['zoom'])
    } else {
      setPagesToDisplay(undefined)
      setControllers(['zoom', 'pages'])
    }
  }, [uploadProcess.uploadType, selectedPage])

  return (
    <>
      <ErrorBoundary>
        <ManualDocumentAssignerContext.Provider value={context}>
          <Box
            borderRadius={{ all: 'abs006' }}
            minWidth="s600"
            border={{
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }}
          >
            <AssignerHeader uploadProcess={uploadProcess} />
            <Box flexDirection="row">
              <Box flexDirection="column" style={{ width: '400px' }}>
                <FilesAssigner
                  uploadProcess={uploadProcess}
                  filesData={filesData}
                  onClickPageFile={onClickPageFile}
                />
              </Box>
              <Box
                flexGrow
                flexDirection="column"
                overflowX="hidden"
                overflowY="hidden"
                minHeight="s600"
                maxHeight="s600"
                border={{
                  right: {
                    width: 's1',
                    style: 'solid',
                    color: 'grey400',
                  },
                }}
              >
                {file && (
                  <FileViewer
                    isPending
                    filename={file.filename}
                    mimeType={file.contentType}
                    pagesToDisplay={pagesToDisplay}
                    url={file.url}
                    isUrlAbsolute
                    controllers={controllers}
                  />
                )}
                {!selectedFile && <NotFound />}
              </Box>
            </Box>
            <AssignerFooter
              uploadProcess={uploadProcess}
              onDistribute={() => setShowResumeModal(true)}
            />
          </Box>
          {showResumeModal && (
            <SummaryModal
              uploadProcess={uploadProcess}
              onSuccess={distribute}
              onClose={() => setShowResumeModal(false)}
            />
          )}
        </ManualDocumentAssignerContext.Provider>
      </ErrorBoundary>
      <HintModal />
    </>
  )
}

export default observer(ManualDocumentAssigner)
